import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  History, 
  Search, 
  Star, 
  Copy, 
  Trash2, 
  Filter, 
  Grid3x3, 
  List, 
  Calendar,
  Sparkles,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const AIHistory = () => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, favorites: 0, total_chars: 0 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    favorites_only: false,
    prompt_id: null
  });
  const [pagination, setPagination] = useState({
    limit: 12,
    skip: 0,
    hasMore: true
  });

  useEffect(() => {
    fetchHistory();
    fetchStats();
  }, [filters, pagination.skip, searchQuery]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        skip: pagination.skip.toString(),
        favorites_only: filters.favorites_only.toString()
      });

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      if (filters.prompt_id) {
        params.append('prompt_id', filters.prompt_id);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/history?${params}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      
      if (data.success) {
        setHistory(data.history || []);
        setPagination(prev => ({ ...prev, hasMore: data.history.length === pagination.limit }));
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/history/stats`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleToggleFavorite = async (historyId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/history/${historyId}/favorite`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setHistory(prev => prev.map(item => 
          item.id === historyId ? { ...item, is_favorite: data.is_favorite } : item
        ));
        fetchStats();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleDelete = async (historyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet historique ?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/history/${historyId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      const data = await response.json();
      
      if (data.success) {
        setHistory(prev => prev.filter(item => item.id !== historyId));
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting history:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Texte copié dans le presse-papier !');
    } catch (error) {
      console.error('Error copying text:', error);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination(prev => ({ 
        ...prev, 
        skip: Math.max(0, prev.skip - prev.limit) 
      }));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  if (loading && history.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Chargement de l'historique...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <History className="inline mr-3 text-cyan-400" size={36} />
              Historique Intelligent
            </h1>
            <p className="text-slate-400 text-lg">Retrouvez et réutilisez vos générations AI</p>
          </div>
          
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchHistory}
              className="px-4 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all"
              data-testid="refresh-btn"
            >
              <RefreshCw size={20} />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="px-4 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all"
              data-testid="view-mode-btn"
            >
              {viewMode === 'grid' ? <List size={20} /> : <Grid3x3 size={20} />}
            </motion.button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm mb-1">Total Générations</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center">
                <Sparkles className="text-cyan-400" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm mb-1">Favoris</p>
                <p className="text-3xl font-bold text-white">{stats.favorites}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl flex items-center justify-center">
                <Star className="text-yellow-400" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm mb-1">Caractères Générés</p>
                <p className="text-3xl font-bold text-white">{stats.total_chars.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center">
                <Calendar className="text-purple-400" size={24} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher dans vos générations..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-cyan-500 transition-all"
              data-testid="search-input"
            />
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowFilters(!showFilters)}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              filters.favorites_only 
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' 
                : 'bg-slate-800 text-slate-300 border border-white/10'
            }`}
            data-testid="filter-btn"
          >
            <Filter size={20} className="inline mr-2" />
            Filtres
          </motion.button>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6"
            >
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.favorites_only}
                    onChange={(e) => handleFilterChange('favorites_only', e.target.checked)}
                    className="w-5 h-5 rounded border-2 border-cyan-500 bg-slate-800 checked:bg-cyan-500 cursor-pointer"
                    data-testid="favorites-filter"
                  />
                  <span className="text-slate-300">Favoris uniquement</span>
                  <Star className="text-yellow-400" size={16} />
                </label>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* History Content */}
      <div className="max-w-7xl mx-auto">
        {history.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-12 text-center"
          >
            <History className="mx-auto text-slate-600 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-white mb-2">Aucune génération trouvée</h3>
            <p className="text-slate-400">
              {searchQuery || filters.favorites_only 
                ? 'Essayez de modifier vos filtres de recherche' 
                : 'Commencez par exécuter des prompts pour voir votre historique ici'}
            </p>
          </motion.div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="history-grid">
                {history.map((item, index) => (
                  <HistoryCard 
                    key={item.id} 
                    item={item} 
                    index={index}
                    onToggleFavorite={handleToggleFavorite}
                    onDelete={handleDelete}
                    onCopy={handleCopy}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-4" data-testid="history-list">
                {history.map((item, index) => (
                  <HistoryListItem 
                    key={item.id} 
                    item={item} 
                    index={index}
                    onToggleFavorite={handleToggleFavorite}
                    onDelete={handleDelete}
                    onCopy={handleCopy}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrevPage}
                disabled={pagination.skip === 0}
                className="px-6 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="prev-page-btn"
              >
                <ChevronLeft size={20} className="inline mr-2" />
                Précédent
              </motion.button>

              <span className="text-slate-400">
                {pagination.skip + 1} - {pagination.skip + history.length}
              </span>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextPage}
                disabled={!pagination.hasMore}
                className="px-6 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-page-btn"
              >
                Suivant
                <ChevronRight size={20} className="inline ml-2" />
              </motion.button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// History Card Component (Grid View)
const HistoryCard = ({ item, index, onToggleFavorite, onDelete, onCopy, formatDate }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-200"
      data-testid={`history-card-${index}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-cyan-400" size={16} />
            <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
          </div>
          {item.metadata?.model && (
            <span className="text-xs px-2 py-1 bg-indigo-500/20 text-indigo-400 rounded-md border border-indigo-500/30">
              {item.metadata.model}
            </span>
          )}
        </div>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onToggleFavorite(item.id)}
          className={`p-2 rounded-lg transition-all ${
            item.is_favorite ? 'text-yellow-400' : 'text-slate-500 hover:text-yellow-400'
          }`}
          data-testid={`favorite-btn-${index}`}
        >
          <Star size={20} fill={item.is_favorite ? 'currentColor' : 'none'} />
        </motion.button>
      </div>

      {/* Generated Text Preview */}
      <div className="mb-4">
        <div className="bg-slate-900/50 rounded-xl p-4 max-h-32 overflow-hidden relative">
          <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans line-clamp-4">
            {item.generated_text}
          </pre>
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-slate-900/80 to-transparent" />
        </div>
        <p className="text-xs text-slate-500 mt-2">{item.generated_text.length} caractères</p>
      </div>

      {/* Variables */}
      {item.variables && Object.keys(item.variables).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {Object.entries(item.variables).slice(0, 3).map(([key, value]) => (
            <span key={key} className="text-xs px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-md border border-cyan-500/30">
              {key}: {String(value).substring(0, 20)}{String(value).length > 20 ? '...' : ''}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onCopy(item.generated_text)}
          className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all text-sm"
          data-testid={`copy-btn-${index}`}
        >
          <Copy size={16} className="inline mr-2" />
          Copier
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onDelete(item.id)}
          className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-all"
          data-testid={`delete-btn-${index}`}
        >
          <Trash2 size={16} />
        </motion.button>
      </div>
    </motion.div>
  );
};

// History List Item Component (List View)
const HistoryListItem = ({ item, index, onToggleFavorite, onDelete, onCopy, formatDate }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-200 flex items-center gap-4"
      data-testid={`history-list-${index}`}
    >
      {/* Favorite Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => onToggleFavorite(item.id)}
        className={`p-2 rounded-lg transition-all ${
          item.is_favorite ? 'text-yellow-400' : 'text-slate-500 hover:text-yellow-400'
        }`}
      >
        <Star size={18} fill={item.is_favorite ? 'currentColor' : 'none'} />
      </motion.button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
          {item.metadata?.model && (
            <span className="text-xs px-2 py-1 bg-indigo-500/20 text-indigo-400 rounded-md border border-indigo-500/30">
              {item.metadata.model}
            </span>
          )}
          <span className="text-xs text-slate-500">{item.generated_text.length} caractères</span>
        </div>
        <p className="text-sm text-slate-300 truncate">{item.generated_text}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onCopy(item.generated_text)}
          className="px-3 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-sm"
        >
          <Copy size={16} />
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onDelete(item.id)}
          className="px-3 py-2 bg-red-500/10 text-red-400 rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-all"
        >
          <Trash2 size={16} />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default AIHistory;
